import { ThemeProvider } from '@mui/system'
import { Route, Routes, useLocation } from 'react-router'
import { Layout } from 'Components/Layout'
import { NotAllowed, ProtectedRoute } from 'Routes/ProtectedRoute'
import * as AppRoutes from 'Pages/Routes'
import { mieccTheme } from 'theme'
import { AboutPage } from 'Pages/AboutPage'
import { UserManagement } from 'Pages/UserManagement'
import { CareNetworkManagement } from 'Pages/CareNetworkManagement'
import { CareNetworkDetail } from 'Pages/CareNetworkManagement/CareNetworkDetail/Index'
import { ApplicationSettings } from 'Pages/ApplicationSettings'
import { ISDManagement } from 'Pages/ISDManagement'
import { StyledEngineProvider } from '@mui/material/styles'
import { PermissionsService as PS } from 'services/permissions-service'
import { PlaceHolder } from 'Pages/Placeholder'
import { ISDSettings } from 'Pages/ISDSettings'
import { ChangePassword } from 'Pages/ChangePassword'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import 'Pages/ToastifyOverrides.css'
import { SettingsSplashPage } from 'Pages/ServiceProvider/SettingsSplash'
import { ServiceSplashPage } from 'Pages/ServiceProvider/Services/ServicesSplash'
import { ISDServiceProviderManagement } from 'Pages/ISD/ServiceProviderManagement'
import { ServiceProviderStaffList } from 'Pages/ServiceProvider/Staff/SettingsStaff'
import { FacilitiesListPage } from 'Pages/ServiceProvider/Facilities/FacilitiesList'
import { SessionBlockList } from 'Pages/ServiceProvider/Services/SessionBlockList'
import { SessionList } from 'Pages/ServiceProvider/Services/SessionList'
import { useEffect } from 'react'
import { sendPost } from 'hooks/use-fetch'
import { useAuth } from 'hooks/use-auth'
import { AccountProvider } from 'ContextProviders/CurrentAccount'
import { SessionTabs } from 'Pages/ServiceProvider/Services/SessionTabs'
import { ReferralSplashScreen } from 'Pages/Referral'
import { EnrollmentSplashPage } from 'Pages/ServiceProvider/Enrollments/EnrollmentSplash'
import { ReferalTabs } from 'Pages/Referral/ReferralTabs'
import { ErrorProvider } from 'ContextProviders/ErrorProvider'
import { EnrollmentList } from 'Pages/ServiceProvider/Enrollments/EnrollmentList'
import { FacilityPage } from 'Pages/ServiceProvider/Facilities/FacilityPage'
import { IncomeApprovalTabList } from 'Pages/IncomeApproval/IncomeApprovalTabList'
import { AccessRequestList } from 'Pages/AccessRequest'
import { UserProfile } from 'Pages/UserManagement/UserProfile'
import { CaregiverPortalIndex } from 'Pages/CaregiverPortal'
import { CaregiverWizard } from 'ContextProviders/CaregiverWizard'
import { CaregiverEnrollments } from 'Pages/CaregiverPortal/InterestForm/CaregiverEnrollments'
import { ProviderSettingsTabs } from 'Pages/ServiceProvider/Settings/ProviderSettingsTabs'
import { useLocalize } from 'hooks/use-localize'
import { CaregiverPostSubmit } from 'Pages/CaregiverPortal/PostSubmit/CaregiverPostSubmit'
import { CaregiverDashboard } from 'Pages/CaregiverPortal/Dashboard/CaregiverDashboard'
import { CaregiverMembers } from 'Pages/CaregiverPortal/Members/CaregiverMembers'
import { CaregiverEnrollmentDetails } from 'Pages/CaregiverPortal/Enrollment/CaregiverEnrollmentDetails'
import { ServiceFinderStep } from 'Pages/CaregiverPortal/ServiceFinder/ServiceFinderStep'
import { AuthProvider } from 'ContextProviders/AuthProvider'
import { ChildDisplay } from 'Pages/CaregiverPortal/Members/ChildDisplay'
import { CaregiverDisplay } from 'Pages/CaregiverPortal/Members/CaregiverDisplay'
import { ChildEdit } from 'Pages/CaregiverPortal/Members/ChildEdit'
import { CaregiverEdit } from 'Pages/CaregiverPortal/Members/CaregiverEdit'
import { ReportList } from 'Pages/Reports/ReportList'
import { GSRPCapacityReport } from 'Pages/Reports/GSRPCapacityReport'
import { RestraintList } from 'Pages/RestraintEvents/RestraintList'
import { FilterOptions } from 'Pages/CaregiverOutreach/FilterOptions'
import { CareManagementList } from 'Pages/CareManagement/CareManagementList'
import { ShortFormSubmitted } from 'Pages/CaregiverPortal/ShortForm/ShortFormSubmitted'
import { RefreshProvider } from 'ContextProviders/RefreshProvider'
import { ChildEditPage } from 'Pages/CareNetworkManagement/CareNetworkDetail/ChildEditPage'
import { CaregiverEdit as CareNetworkCaregiverEdit } from 'Components/CareNetwork/CaregiverEdit'
import { SiblingEdit } from 'Components/CareNetwork/SiblingEdit'
import { EnrollmentPage } from 'Pages/Enrollments/EnrollmentPage'
import { type User } from 'core/types'
import { ProgramVerificationDetails } from 'Components/Program/ProgramVerificationDetails'
import { CareNetworkDetails } from 'Pages/CareManagement/CareNetworkDetails'
import { IncomeApprovalDetail } from 'Pages/IncomeApproval/IncomeApprovalDetail'

function App (): JSX.Element {
  const { user } = useAuth()
  const location = useLocation()
  useLocalize()

  useEffect(() => {
    if (user?.isImpersonating ?? false) {
      void sendPost('/PageVisit/LogPageVisit', { pageUrl: location.pathname })
    }
  }, [location, user])

  const sharedPermission = (user: User): boolean => {
    return PS.CareNetwork(user) || PS.Enrollment(user)
  }
  return (
    <StyledEngineProvider injectFirst>
      <AuthProvider >
        <ThemeProvider theme={mieccTheme}>
          <AccountProvider>
            <CaregiverWizard>
              <RefreshProvider>
                <Layout>
                  <ToastContainer
                    position="top-center"
                    autoClose={5000}
                    hideProgressBar
                    closeOnClick
                    draggable={false}
                    theme="colored"
                  />
                  <Routes>
                    <Route
                      index={true}
                      path={AppRoutes.LANDING_PATH}
                      element={
                        <AboutPage />
                        }
                    />
                    <Route
                      path={AppRoutes.UNAUTHORIZED}
                      element={
                        <NotAllowed />
                        }
                    />
                    <Route
                      path={AppRoutes.PUBLIC_GSRP_CAPACITY_REPORT}
                      element={<GSRPCapacityReport />}
                    />
                    <Route
                      element={
                        <ProtectedRoute allow={(user?) => { return user !== null }} />
                        }
                    >
                      <Route
                        path={AppRoutes.MY_PROFILE}
                        element={<UserProfile />}
                      />
                      <Route
                        path={AppRoutes.CHANGE_PASSWORD}
                        element={
                          <ChangePassword />
                          }
                      />
                    </Route>

                    <Route
                      element={
                        <ProtectedRoute allow={PS.CaregiverPortal} noContainer={true} allowAnonymous={true} />
                        }
                    >
                      <Route
                        path={AppRoutes.CAREGIVER_PORTAL}
                        element={<CaregiverPortalIndex />}
                      />
                      <Route
                        element={
                          <ProtectedRoute allow={PS.CaregiverPortal_Enrollments} noContainer={true} allowAnonymous={true} />
                          }
                      >
                        <Route
                          path={AppRoutes.CAREGIVER_ENROLLMENTS}
                          element={
                            <CaregiverEnrollments />
                            }
                        />
                        <Route
                          path={AppRoutes.CAREGIVER_PORTAL_SUBMITTED}
                          element={
                            <CaregiverPostSubmit />
                            }
                        />
                        <Route
                          path={AppRoutes.CAREGIVER_PORTAL_SHORT_FORM_SUBMITTED}
                          element={
                            <ShortFormSubmitted />
                            }
                        />
                      </Route>
                      <Route
                        path={AppRoutes.CAREGIVER_PORTAL_DASHBOARD}
                        element={<CaregiverDashboard />}
                      />
                      <Route
                        path={AppRoutes.CAREGIVER_PORTAL_MEMBERS}
                        element={<CaregiverMembers />}
                      />
                      <Route
                        path={AppRoutes.CAREGIVER_PORTAL_MEMBERS_CHILD_DETAILS}
                        element={<ChildDisplay />}
                      />
                      <Route
                        path={AppRoutes.CAREGIVER_PORTAL_MEMBERS_EDIT_CHILD}
                        element={<ChildEdit />}
                      />
                      <Route
                        path={AppRoutes.CAREGIVER_PORTAL_MEMBERS_CAREGIVER_DETAILS}
                        element={<CaregiverDisplay />}
                      />
                      <Route
                        path={AppRoutes.CAREGIVER_PORTAL_MEMBERS_EDIT_CAREGIVER}
                        element={<CaregiverEdit />}
                      />
                      <Route
                        path={AppRoutes.CAREGIVER_PORTAL_ENROLLMENT_DETAILS}
                        element={<CaregiverEnrollmentDetails />}
                      />
                      <Route
                        path={AppRoutes.CAREGIVER_PORTAL_SERVICE_FINDER}
                        element={<ServiceFinderStep />}
                      />
                    </Route>

                    <Route element={<ProtectedRoute allow={sharedPermission} />}>
                      <Route
                        path={AppRoutes.CARENETWORK_MANAGEMENT_DETAILS}
                        element={<CareNetworkDetail />}
                      />
                      <Route
                        path={AppRoutes.CARENETWORK_MANAGEMENT_EDIT}
                        element={<ChildEditPage />}
                      />
                      <Route
                        path={AppRoutes.SIBLING_EDIT}
                        element={<SiblingEdit />}
                      />
                      <Route path={AppRoutes.CAREGIVER_EDIT} element={<CareNetworkCaregiverEdit />} />
                      <Route path={AppRoutes.PROGRAM_VERIFICATION} element={<ProgramVerificationDetails />} />
                      <Route path={AppRoutes.CARE_NETWORK_DETAILS} element={<CareNetworkDetails />} />
                      <Route
                        path={AppRoutes.ENROLLMENT}
                        element={<EnrollmentPage />}
                      />

                    </Route>
                    <Route
                      element={
                        <ProtectedRoute allow={PS.CareNetwork} />
                        }
                    >
                      <Route
                        path={AppRoutes.FIND_CHILDREN}
                        element={<CareNetworkManagement />}
                      />
                      <Route
                        element={
                          <ProtectedRoute allow={PS.CareNetwork_Caregiver} />
                          }
                      >
                        <Route
                          path={AppRoutes.CAREGIVER_MANAGEMENT}
                          element={<PlaceHolder title={'Caregiver Management'} />}
                        />
                        <Route path={AppRoutes.CAREGIVER_EDIT} element={<CareNetworkCaregiverEdit />} />
                      </Route>

                      <Route
                        element={
                          <ProtectedRoute allow={PS.CareNetwork_NetworkMembers} />
                          }
                      >
                        <Route
                          path={AppRoutes.CARENETWORK_MEMBERS}
                          element={<PlaceHolder title={'Network Members'} />}
                        />
                      </Route>

                      <Route
                        element={
                          <ProtectedRoute allow={PS.CareNetwork_Documentation} />
                          }
                      >
                        <Route
                          path={AppRoutes.DOCUMENTATION}
                          element={<PlaceHolder title={'Documentation'} />}
                        />
                      </Route>

                      <Route
                        element={
                          <ProtectedRoute allow={PS.CareNetwork_IncomeRequest} />
                          }
                      >
                        <Route
                          path={AppRoutes.INCOME_REQUEST}
                          element={<PlaceHolder title={'Income Request'} />}
                        />
                      </Route>

                      <Route
                        element={
                          <ProtectedRoute allow={PS.CareNetwork_SensitiveDetails} />
                          }
                      >
                        <Route
                          path={AppRoutes.SENSITIVE_DETAILS}
                          element={<PlaceHolder title={'Sensitive Details'} />}
                        />
                      </Route>

                      <Route
                        element={
                          <ProtectedRoute allow={PS.CareNetwork_Enrollment} />
                          }
                      >
                        <Route
                          path={AppRoutes.ENROLLMENT_DETAILS}
                          element={<PlaceHolder title={'Enrollment Details'} />}
                        />
                      </Route>

                      <Route
                        element={
                          <ProtectedRoute allow={PS.CareNetwork_Communication} />
                          }
                      >
                        <Route
                          path={AppRoutes.COMMUNICATION}
                          element={<PlaceHolder title={'Communication'} />}
                        />
                      </Route>
                    </Route>

                    <Route
                      element={<ProtectedRoute allow={PS.Enrollment} />}
                    >
                      <Route
                        path={AppRoutes.REFERRAL_MANAGEMENT}
                        element={<ReferralSplashScreen />}
                      />
                      <Route
                        path={AppRoutes.REFERRALS}
                        element={<ReferalTabs />}
                      />
                      <Route
                        path={AppRoutes.ENROLLMENT_MANAGEMENT}
                        element={<EnrollmentSplashPage />}
                      />
                      <Route
                        path={AppRoutes.ENROLLMENT_MANAGEMENT_DETAILS}
                        element={<EnrollmentList />}
                      />
                      <Route
                        path={AppRoutes.ENROLLMENT_DETAILS}
                        element={<PlaceHolder title='enrollment details' />}
                      />
                    </Route>
                    <Route
                      element={<ProtectedRoute allow={PS.Reports} />}
                    >
                      <Route
                        path={AppRoutes.REPORTS}
                        element={<ReportList />}
                      />
                    </Route>
                    <Route
                      path={AppRoutes.CARE_MANAGEMENT}
                      element={<CareManagementList />}
                    />
                    <Route
                      element={<ProtectedRoute allow={PS.WaiverApprovals} />}
                    >
                      <Route
                        path={AppRoutes.INCOME_APPROVALS}
                        element={<IncomeApprovalTabList />}
                      />
                    </Route>
                    <Route
                      path={AppRoutes.INCOME_APPROVAL_DETAILS}
                      element={<IncomeApprovalDetail />}
                    />
                    <Route
                      path={AppRoutes.CAREGIVER_OUTREACH}
                      element={<FilterOptions />}
                    />
                    <Route
                      path={AppRoutes.RESTRAINT_LIST}
                      element={<RestraintList />}
                    />
                    <Route
                      element={<ProtectedRoute allow={PS.ISDs_Approvals} />}
                    >
                      <Route
                        path={AppRoutes.ACCESS_REQUESTS}
                        element={<AccessRequestList />}
                      />
                    </Route>
                    <Route
                      element={<ProtectedRoute allow={PS.ServiceProviders} />}
                    >
                      <Route
                        path={AppRoutes.SERVICE_PROVIDER_ADMIN_SERVICES}
                        element={<ServiceSplashPage />}
                      />
                      <Route
                        path={AppRoutes.SERVICE_PROVIDER_ADMIN_SESSION_BLOCK}
                        element={<SessionBlockList />}
                      />
                      <Route
                        path={AppRoutes.SERVICE_PROVIDER_ADMIN_SESSION_BLOCK_SESSION}
                        element={<SessionList />}
                      />
                      <Route
                        path={AppRoutes.SERVICE_PROVIDER_ADMIN_SESSION_BLOCK_SESSION_DETAILS}
                        element={<SessionTabs />}
                      />
                      <Route
                        path={AppRoutes.SERVICE_PROVIDER_ADMIN_SETTINGS}
                        element={<SettingsSplashPage />}
                      />
                      <Route
                        path={AppRoutes.SERVICE_PROVIDER_ADMIN_DETAILS}
                        element={<ProviderSettingsTabs />}
                      />
                      <Route
                        path={AppRoutes.SERVICE_PROVIDER_ADMIN_FACILITIES}
                        element={<FacilitiesListPage />}
                      />
                      <Route
                        path={AppRoutes.SERVICE_PROVIDER_ADMIN_FACILITIES_DETAILS}
                        element={<FacilityPage />}
                      />
                      <Route
                        path={AppRoutes.SERVICE_PROVIDER_ADMIN_STAFF}
                        element={<ServiceProviderStaffList />}
                      />
                    </Route>

                    <Route
                      element={<ProtectedRoute allow={PS.ISDs} />}
                    >
                      <Route
                        path={AppRoutes.ISD_SETTINGS}
                        element={<ISDSettings />}
                      />
                      <Route
                        path={AppRoutes.SERVICE_PROVIDER_MANAGEMENT}
                        element={<ISDServiceProviderManagement />}
                      />
                      <Route
                        element={<ProtectedRoute allow={PS.ISDs_Approvals} />}
                      >
                        <Route
                          path={AppRoutes.ISD_INCOME_APPROVALS}
                          element={<IncomeApprovalTabList />}
                        />
                        <Route
                          path={AppRoutes.FUNDING_ALLOCATION}
                          element={<PlaceHolder title='Funding Allocation' />}
                        />

                      </Route>

                    </Route>

                    <Route
                      element={<ProtectedRoute allow={PS.MiECCAdmin} />}
                    >
                      <Route
                        path={AppRoutes.ISD_MANAGEMENT}
                        element={
                          <ISDManagement />
                          }
                      />
                      <Route
                        path={AppRoutes.SERVICE_PROVIDER_MANAGEMENT}
                        element={
                          <ISDServiceProviderManagement />
                          }
                      />
                      <Route
                        path={AppRoutes.APPLICATION_SETTINGS}
                        element={
                          <ApplicationSettings />
                          }
                      />
                    </Route>
                    <Route
                      element={<ProtectedRoute allow={PS.Users} />}
                    >
                      <Route
                        path={AppRoutes.USER_MANAGEMENT}
                        element={
                          <ErrorProvider>
                            <UserManagement />
                          </ErrorProvider>
                          }
                      />
                    </Route>

                  </Routes>
                </Layout>
              </RefreshProvider>
            </CaregiverWizard>
          </AccountProvider>
        </ThemeProvider>
      </AuthProvider>
    </StyledEngineProvider>
  )
}

export default App
